// ABOUTME: Layout shell for unauthenticated users with public navigation
// ABOUTME: Redirects authenticated users to /home

import { useEffect, useState } from 'react'
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import {
  HomeIcon,
  AcademicCapIcon,
  ArrowRightEndOnRectangleIcon,
} from '@heroicons/react/24/outline'
import type { User } from '@/providers'
import { getAuthService } from '@/services/AuthService'
import { ROUTES } from '@/constants/routes'

const navigation = [
  { name: 'Home', href: ROUTES.LANDING, icon: HomeIcon },
  { name: 'Learn', href: ROUTES.EDUCATION, icon: AcademicCapIcon },
  { name: 'Sign In', href: ROUTES.AUTH, icon: ArrowRightEndOnRectangleIcon },
]

export default function PublicLayout() {
  const location = useLocation()
  const [user, setUser] = useState<User | null | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const authService = getAuthService()
    authService.getCurrentUser().then((u) => {
      setUser(u)
      setLoading(false)
    })
    const unsubscribe = authService.onAuthStateChange(setUser)
    return () => unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-fg-tertiary text-sm">Loading...</p>
      </div>
    )
  }

  if (user) {
    return <Navigate to={ROUTES.HOME} replace />
  }

  return (
    <div className="flex min-h-full flex-col bg-bg text-fg-primary">
      <Outlet />
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-bg/90 backdrop-blur-md safe-bottom">
        <div className="flex items-center justify-around h-16 border-t" style={{ borderColor: 'var(--glass-border)' }}>
          {navigation.map((item) => {
            const active = location.pathname === item.href
            const Icon = item.icon
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`relative flex flex-col items-center gap-0.5 px-3 py-1.5 transition-colors ${
                  active ? 'text-fg-primary' : 'text-fg-tertiary'
                }`}
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={active ? 2 : 1.5} />
                <span className="text-[10px] font-medium tracking-wide">{item.name}</span>
                {active && (
                  <span
                    className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-5 h-[1.5px] rounded-full"
                    style={{ background: 'var(--color-primary)' }}
                  />
                )}
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
